'use client';

import { memo, useState, useEffect, useMemo, useCallback } from 'react';
import { Drawer, Box, Stack, Divider, CircularProgress } from '@mui/material';
import { themeColors } from '../../../theme/theme';
import ParcelJourneyTimeline from '../../ParcelJourneyTimeline';
import { getCoordsFromLocation } from '../../../utils/locationUtils';
import useApiCall from '../../../services/api/useApiCall';
import { buildApiConfig } from '../../../services/api/apiClient';
import { endPointEnums } from '../../../services/api/endpoints';
import { SOCKET_EVENTS } from '../../../hooks/socket';
import useWindowEvent from '../../../hooks/useWindowEvent';
import { useETACalculation, formatETA } from '../../../hooks/useETACalculation';
import {
  calculateDistance,
  calculateETA as calcETA,
  formatDuration,
} from '../../../common/maps/utils/distanceCalculator';

// Sub-components
import SheetHeader from './SheetHeader';
import AddressSection from './AddressSection';
import LiveTrackingCard from './LiveTrackingCard';
import ParcelDetailsSection from './ParcelDetailsSection';
import RiderSection from './RiderSection';
import ReceiverSection from './ReceiverSection';
import { useTranslation } from '@/kit/useTranslation';

/**
 * Get status color based on status string
 */
const getStatusColor = (status) => {
  const statusLower = status?.toLowerCase() || '';
  if (statusLower.includes('transit')) return '#000000';
  if (statusLower.includes('cancelled')) return '#f44336';
  if (statusLower.includes('delivered')) return '#000000';
  if (statusLower.includes('pending')) return '#2d2d2d';
  if (statusLower.includes('assigned')) return '#1a1a1a';
  if (statusLower.includes('picked')) return '#673ab7';
  if (statusLower.includes('out for delivery')) return '#ff5722';
  return themeColors.primaryColor;
};

/**
 * Extract coordinates from address objects
 */
const getAddressCoords = (address) => {
  if (!address) return null;
  if (address.lat && address.lng) return { lat: address.lat, lng: address.lng };
  if (address.latitude && address.longitude)
    return { lat: address.latitude, lng: address.longitude };
  return null;
};

/**
 * Unified BookingDetailsSheet component
 * Displays booking/parcel details in a bottom sheet drawer
 *
 * @param {Object} booking - Parcel/booking data (uses backend field names: parcelId, parcelStatus, etc.)
 * @param {Function} onClose - Close handler
 * @param {Function} onRefresh - Optional refresh handler
 */
const UnifiedBookingDetailsSheet = memo(({ booking, onClose, onRefresh }) => {
  const { t } = useTranslation();
  const [legs, setLegs] = useState([]);
  const [loadingLegs, setLoadingLegs] = useState(false);
  const { makeApiCall } = useApiCall();

  // Use backend field names directly - parcelId is the primary ID field
  const parcelId = booking?.parcelId || booking?.id;

  // Fetch legs for multi-stage parcels
  useEffect(() => {
    const fetchLegs = async () => {
      if (!parcelId) return;

      setLoadingLegs(true);
      try {
        const config = buildApiConfig(endPointEnums.GET_LEGS_BY_PARCEL, {
          parcelId,
        });
        const response = await makeApiCall(config);
        if (response?.data) {
          setLegs(response.data);
        }
      } catch (error) {
        // Error handled silently
      } finally {
        setLoadingLegs(false);
      }
    };

    fetchLegs();
  }, [parcelId, makeApiCall]);

  // Listen for parcel updates via socket events
  const handleParcelUpdate = useCallback(
    (event) => {
      const { parcelId: updatedId } = event.detail || {};

      if (updatedId && parcelId && updatedId === parcelId) {
        const fetchLegs = async () => {
          try {
            const config = buildApiConfig(endPointEnums.GET_LEGS_BY_PARCEL, {
              parcelId,
            });
            const response = await makeApiCall(config);
            if (response?.data) {
              setLegs(response.data);
            }
          } catch (error) {
            // Error handled silently
          }
        };
        fetchLegs();

        if (onRefresh) {
          onRefresh();
        }
      }
    },
    [parcelId, makeApiCall, onRefresh]
  );

  useWindowEvent(SOCKET_EVENTS.PARCEL_UPDATED, handleParcelUpdate);

  if (!booking) return null;

  // Extract data using backend field names (parcel* prefix)
  const status = booking.parcelStatus || booking.status;
  const statusColor = getStatusColor(status);
  const isMultiStage = booking.parcelIsMultiStage || booking.isMultiStage || false;
  const totalLegs = booking.parcelTotalLegs || booking.totalLegs || 1;
  const isFirmTrip = booking.parcelIsFirmTrip || booking.isFirmTrip || false;
  const fromAddress = booking.parcelPickupAddress || booking.from;
  const toAddress = booking.parcelDeliveryAddress || booking.to;
  const itemType = booking.parcelParcelType || booking.itemType;
  const weight = booking.parcelWeightKg || booking.parcelWeight || booking.weight;
  const price = booking.parcelPrice || booking.price;
  const receiverName = booking.parcelDeliveryAddress?.name || booking.receiverName;
  const receiverPhone = booking.parcelDeliveryAddress?.phone || booking.receiverPhone;
  const riderName = booking.parcelRiderName || booking.riderName;
  const riderPhone = booking.parcelRiderPhone || booking.riderPhone;
  const dateTime = booking.parcelCreatedAt || booking.dateTime;

  // Get rider's last known location from active leg
  const activeLeg = useMemo(() => {
    if (!legs.length) return null;
    return legs.find((leg) =>
      ['ASSIGNED', 'PICKED_UP', 'IN_TRANSIT', 'OUT_FOR_DELIVERY'].includes(
        leg.parcelLegsStatus?.toUpperCase()
      )
    );
  }, [legs]);

  const riderLocation = useMemo(() => {
    if (!activeLeg?.parcelLegsLastKnownLocation) return null;
    return getCoordsFromLocation(activeLeg.parcelLegsLastKnownLocation);
  }, [activeLeg]);

  const destinationCoords = useMemo(() => {
    if (activeLeg?.parcelLegsDropAddress) {
      return getAddressCoords(activeLeg.parcelLegsDropAddress);
    }
    return getAddressCoords(toAddress);
  }, [activeLeg, toAddress]);

  // Calculate ETA using Google Maps
  const isActiveDelivery = ['ASSIGNED', 'PICKED_UP', 'IN_TRANSIT', 'OUT_FOR_DELIVERY'].includes(
    status?.toUpperCase()
  );
  const {
    eta,
    durationText,
    distanceText,
    loading: etaLoading,
    refresh: refreshETA,
  } = useETACalculation(riderLocation, destinationCoords, isActiveDelivery && !!riderLocation);

  // Fallback ETA when Google ETA not available (straight-line approximation)
  const computedEtaFallback = useMemo(() => {
    if (eta) return formatETA(eta);
    if (etaLoading) return null;
    if (riderLocation && destinationCoords) {
      const dist = calculateDistance(riderLocation, destinationCoords);
      const mins = calcETA(dist);
      return mins ? formatDuration(mins) : null;
    }
    return null;
  }, [eta, etaLoading, riderLocation, destinationCoords]);

  const handleDownloadInvoice = () => {
    window.open(`/api/invoice/${parcelId}`, '_blank');
  };

  return (
    <Drawer
      anchor="bottom"
      open={!!booking}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          maxHeight: '90vh',
          maxWidth: { xs: '100%', sm: 720 },
          width: '100%',
          mx: 'auto',
          overflowX: 'hidden',
        },
      }}
    >
      {/* Drag Handle */}
      <Box
        sx={{
          width: 40,
          height: 5,
          bgcolor: '#e5e7eb',
          borderRadius: 999,
          mx: 'auto',
          mt: 1.5,
          mb: 1,
        }}
      />

      {/* Header */}
      <SheetHeader
        parcelId={parcelId}
        status={status}
        statusColor={statusColor}
        price={price}
        dateTime={dateTime}
        isFirmTrip={isFirmTrip}
        isMultiStage={isMultiStage}
        totalLegs={totalLegs}
        onClose={onClose}
        onDownloadInvoice={handleDownloadInvoice}
      />

      {/* Main Content */}
      <Box
        sx={{
          px: 2,
          py: 2,
          overflowY: 'auto',
          maxHeight: 'calc(90vh - 120px)',
        }}
      >
        <Stack spacing={2.5}>
          {/* Route Information */}
          <AddressSection fromAddress={fromAddress} toAddress={toAddress} />

          {/* Live Location & ETA Card */}
          {isActiveDelivery && (riderLocation || durationText) && (
            <LiveTrackingCard
              statusColor={statusColor}
              durationText={durationText}
              distanceText={distanceText}
              eta={eta}
              etaLoading={etaLoading}
              riderLocation={riderLocation}
              riderName={activeLeg?.parcelLegsRiderName || riderName}
              onRefresh={refreshETA}
            />
          )}

          <Divider sx={{ my: 0.5 }} />

          {/* Parcel Details */}
          <ParcelDetailsSection itemType={itemType} weight={weight} />

          {/* Rider Information */}
          {(riderName || legs.some((leg) => leg.parcelLegsRiderName)) && (
            <>
              <Divider sx={{ my: 0.5 }} />
              <RiderSection
                riderName={riderName}
                riderPhone={riderPhone}
                statusColor={statusColor}
                isMultiStage={isMultiStage}
                legs={legs}
              />
            </>
          )}

          {/* Receiver Details */}
          {receiverName && (
            <>
              <Divider sx={{ my: 0.5 }} />
              <ReceiverSection receiverName={receiverName} receiverPhone={receiverPhone} />
            </>
          )}

          {/* Journey Timeline */}
          {(legs.length > 0 || loadingLegs) && (
            <>
              <Divider sx={{ my: 0.5 }} />
              <Box>
                <Box
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: 'text.secondary',
                    mb: 1,
                  }}
                >
                  Journey Timeline
                </Box>
                {loadingLegs ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <ParcelJourneyTimeline
                    legs={legs}
                    parcel={booking}
                    liveTracking={{
                      eta: eta ? formatETA(eta) : computedEtaFallback,
                      etaLoading,
                      location: activeLeg?.parcelLegsLastKnownLocation,
                      updatedAt: activeLeg?.parcelLegsUpdatedAt,
                    }}
                  />
                )}
              </Box>
            </>
          )}
        </Stack>
      </Box>
    </Drawer>
  );
});

UnifiedBookingDetailsSheet.displayName = 'UnifiedBookingDetailsSheet';

export default UnifiedBookingDetailsSheet;
